import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ShoppingBag } from 'lucide-react'
import { Spinner } from '../../components/ui/Spinner'
import { EmptyState } from '../../components/ui/EmptyState'
import { useCurrency } from '../../hooks/useCurrency'
import { useAuthStore } from '../../store/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import { NotAuthorizedView } from '../../components/permissions/NotAuthorizedView'
import { supabase } from '../../lib/supabase'
import { cn } from '../../utils/cn'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

type SessionRow = {
  id: string
  user_id: string
  session_number: string | null
  status: string
  opened_at: string
  closed_at: string | null
  opening_cash: number
  total_sales: number
  total_transactions: number
  warehouses: { id: string; name: string } | null
}

type CashierRow = {
  user_id: string
  username: string | null
  full_name: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
}

function resolveName(p: CashierRow | undefined): string {
  if (!p) return 'Unknown'
  if (p.username) return p.username
  if (p.full_name) return p.full_name
  if (p.first_name && p.last_name) return `${p.first_name} ${p.last_name}`
  if (p.email) return p.email.split('@')[0]
  return 'Unknown'
}

export default function POSSessionsPage() {
  const { tenant, user } = useAuthStore()
  const { format } = useCurrency()
  const { viewScope } = usePermissions()
  const scope = viewScope('pos')
  const ownerId = scope === 'own' ? (user?.id ?? '') : undefined
  const tid = tenant?.id ?? ''

  const { data: sessions = [], isLoading } = useQuery<SessionRow[]>({
    queryKey: ['pos-sessions-history', tid, ownerId],
    enabled: !!tid && scope !== 'none',
    queryFn: async () => {
      let q = db('pos_sessions')
        .select(`
          id, user_id, session_number, status, opened_at, closed_at,
          opening_cash, total_sales, total_transactions,
          warehouses(id, name)
        `)
        .eq('tenant_id', tid)
        .order('opened_at', { ascending: false })
        .limit(200)
      if (ownerId) q = q.eq('user_id', ownerId)
      const { data } = await q
      return (data ?? []) as SessionRow[]
    },
  })

  const userIds = useMemo(
    () => Array.from(new Set(sessions.map(s => s.user_id))),
    [sessions],
  )

  const { data: cashiers = [] } = useQuery<CashierRow[]>({
    queryKey: ['pos-sessions-cashiers', tid, userIds.join(',')],
    enabled: !!tid && userIds.length > 0,
    queryFn: async () => {
      const { data } = await db('tenant_users')
        .select('user_id, username, full_name, first_name, last_name, email')
        .eq('tenant_id', tid)
        .in('user_id', userIds)
      return (data ?? []) as CashierRow[]
    },
  })

  const cashierMap = useMemo(() => {
    const m: Record<string, CashierRow> = {}
    cashiers.forEach(c => { m[c.user_id] = c })
    return m
  }, [cashiers])

  if (scope === 'none') return <NotAuthorizedView />

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Link to="/pos/open" className="btn-primary">
          <ShoppingBag className="h-4 w-4" /> Open New Session
        </Link>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : sessions.length === 0 ? (
          <EmptyState
            title="No POS sessions yet"
            description="Open one to start tracking sales by session."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  {['Session #', 'Cashier', 'Location', 'Opening Cash', 'Total Sales', 'Txns', 'Opened', 'Closed', 'Status'].map(h => (
                    <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sessions.map(s => (
                  <tr key={s.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2.5">
                      <Link to={`/pos/sessions/${s.id}/report`} className="font-mono text-xs text-brand-600 hover:underline">
                        {s.session_number ?? '—'}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-gray-700">{resolveName(cashierMap[s.user_id])}</td>
                    <td className="px-3 py-2.5 text-gray-700">{s.warehouses?.name ?? '—'}</td>
                    <td className="px-3 py-2.5 font-medium text-gray-900 whitespace-nowrap">{format(s.opening_cash ?? 0)}</td>
                    <td className="px-3 py-2.5 font-semibold text-gray-900 whitespace-nowrap">{format(s.total_sales ?? 0)}</td>
                    <td className="px-3 py-2.5 text-gray-700">{s.total_transactions ?? 0}</td>
                    <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                      {new Date(s.opened_at).toLocaleString('en-NG', { dateStyle: 'short', timeStyle: 'short' })}
                    </td>
                    <td className="px-3 py-2.5 text-gray-500 whitespace-nowrap text-xs">
                      {s.closed_at
                        ? new Date(s.closed_at).toLocaleString('en-NG', { dateStyle: 'short', timeStyle: 'short' })
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={cn(
                        'badge text-xs',
                        s.status === 'open' ? 'badge-green' : s.status === 'closed' ? 'badge-gray' : 'badge-red'
                      )}>
                        {s.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
